/**
 * iter251 — Activation email funnel: Sent → Opened → Clicked → Activated.
 *
 * Rendered as nested horizontal bars rather than four isolated stat tiles,
 * because the drop-off BETWEEN stages is the thing worth looking at.
 */
import React from 'react';
import { MailCheck, Eye, MousePointerClick, ShieldCheck, Timer } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  if (minutes < 1440) return `${(minutes / 60).toFixed(1)}h`;
  return `${(minutes / 1440).toFixed(1)}d`;
};

const STAGES = [
  { key: 'sent', label: 'Sent', icon: MailCheck, bar: 'bg-slate-600', text: 'text-slate-100' },
  { key: 'opened', label: 'Opened', icon: Eye, bar: 'bg-sky-600', text: 'text-sky-300' },
  { key: 'clicked', label: 'Clicked', icon: MousePointerClick, bar: 'bg-violet-600', text: 'text-violet-300' },
  { key: 'activated', label: 'Activated', icon: ShieldCheck, bar: 'bg-emerald-600', text: 'text-emerald-300' },
];

export const ActivationFunnelCard = ({ funnel }) => {
  const f = funnel || {};
  const sent = f.sent || 0;

  return (
    <Card data-testid="activation-funnel-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-slate-100 flex items-center gap-2">
          <MailCheck className="h-4 w-4 text-sky-400" /> Activation funnel
        </CardTitle>
        <CardDescription className="text-slate-400 text-sm">
          Self-hosted open + click tracking on the activation email only. An open
          needs images to load; a click counts as an open either way.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {STAGES.map(({ key, label, icon: Icon, bar, text }) => {
          const value = f[key] || 0;
          const pct = sent > 0 ? Math.min(100, (value / sent) * 100) : 0;
          return (
            <div key={key} data-testid={`activation-funnel-stage-${key}`}>
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-xs text-slate-400 flex items-center gap-1.5">
                  <Icon className={`h-3.5 w-3.5 ${text}`} /> {label}
                </span>
                <span className="flex items-baseline gap-2">
                  <span
                    className={`text-xl font-bold tabular-nums ${text}`}
                    data-testid={`activation-funnel-${key}-count`}
                  >
                    {value}
                  </span>
                  {key !== 'sent' && (
                    <span className="text-[11px] text-slate-500 tabular-nums w-12 text-right">
                      {pct.toFixed(0)}%
                    </span>
                  )}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-[#0A1024] overflow-hidden">
                <div
                  className={`h-full rounded-full ${bar} transition-[width] duration-700 ease-out`}
                  style={{ width: `${sent > 0 ? pct : 0}%` }}
                />
              </div>
            </div>
          );
        })}

        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#1F2A4D]">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
              <Timer className="h-3 w-3" /> Median time to activate
            </p>
            <p
              className="text-lg font-semibold text-slate-100 mt-0.5 tabular-nums"
              data-testid="activation-funnel-median"
            >
              {formatDuration(f.median_minutes_to_activate)}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500">
              Activation rate
            </p>
            <p
              className="text-lg font-semibold text-emerald-300 mt-0.5 tabular-nums"
              data-testid="activation-funnel-rate"
            >
              {(f.activation_rate || 0).toFixed(1)}%
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ActivationFunnelCard;
